import React, { useState, useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';

const SecurityCompliance = () => {
  const [activeSection, setActiveSection] = useState('authentication');

  // Animate security cards on scroll
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
        }
      });
    }, { threshold: 0.1 });

    const cards = document.querySelectorAll('.security-card');
    cards.forEach(card => {
      card.style.opacity = '0';
      card.style.transform = 'translateY(20px)';
      card.style.transition = 'all 0.6s ease-out';
      observer.observe(card);
    });

    return () => observer.disconnect();
  }, [activeSection]); // Re-run when section changes

  const showSection = (sectionId) => {
    setActiveSection(sectionId);
  };

  const exportAudit = () => {
    alert('Export des logs d\'audit en cours... Le fichier sera téléchargé dans quelques instants.');
  };

  const handleRiskCellClick = (level) => {
    const riskLevels = {
      'L': 'Faible - Surveillance normale',
      'M': 'Moyen - Attention particulière',
      'H': 'Élevé - Actions correctives requises',
      'C': 'Critique - Intervention immédiate'
    };
    alert(`Niveau de risque: ${riskLevels[level]}`);
  };

  return (
    <>
      <style>{`
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: #0a0a0a;
            color: #ffffff;
            line-height: 1.6;
        }
        
        /* Navigation */
        nav {
            position: fixed;
            top: 0;
            width: 100%;
            background: rgba(10, 10, 10, 0.95);
            backdrop-filter: blur(10px);
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            z-index: 1000;
            padding: 1rem 2rem;
        }
        
        .nav-container {
            max-width: 1400px;
            margin: 0 auto;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        
        .back-link {
            color: #667eea;
            text-decoration: none;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            transition: gap 0.3s;
        }
        
        .back-link:hover {
            gap: 1rem;
        }
        
        /* Main Container */
        .container {
            max-width: 1400px;
            margin: 0 auto;
            padding: 100px 2rem 2rem;
        }
        
        /* Header */
        .page-header {
            text-align: center;
            margin-bottom: 4rem;
        }
        
        .page-title {
            font-size: 3.5rem;
            margin-bottom: 1rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .page-subtitle {
            font-size: 1.3rem;
            color: #888;
        }
        
        /* Security Status */
        .security-status {
            background: rgba(67, 232, 169, 0.1);
            border: 1px solid rgba(67, 232, 169, 0.3);
            border-radius: 16px;
            padding: 2rem;
            margin-bottom: 4rem;
            text-align: center;
        }
        
        .status-icon {
            font-size: 4rem;
            margin-bottom: 1rem;
        }
        
        .status-title {
            font-size: 1.5rem;
            font-weight: bold;
            margin-bottom: 0.5rem;
            color: #67e8a9;
        }
        
        .status-description {
            color: #bbb;
            font-size: 1.1rem;
        }
        
        .security-score {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 2rem;
            margin-top: 2rem;
        }
        
        .score-item {
            text-align: center;
        }
        
        .score-value {
            font-size: 2.5rem;
            font-weight: bold;
            background: linear-gradient(135deg, #67e8a9 0%, #48bb78 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .score-label {
            color: #888;
            font-size: 0.9rem;
        }
        
        /* Section Tabs */
        .section-tabs {
            display: flex;
            justify-content: center;
            gap: 1rem;
            margin-bottom: 3rem;
            flex-wrap: wrap;
        }
        
        .tab-button {
            padding: 0.8rem 2rem;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            color: #888;
            cursor: pointer;
            transition: all 0.3s;
            font-size: 1rem;
            font-weight: 600;
        }
        
        .tab-button:hover {
            background: rgba(255, 255, 255, 0.1);
            color: white;
        }
        
        .tab-button.active {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border-color: transparent;
        }
        
        /* Content Sections */
        .content-section {
            display: none;
            animation: fadeIn 0.5s ease;
        }
        
        .content-section.active {
            display: block;
        }
        
        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        
        /* Security Features Grid */
        .security-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
            gap: 2rem;
            margin: 3rem 0;
        }
        
        .security-card {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            transition: all 0.3s;
            position: relative;
            overflow: hidden;
        }
        
        .security-card::before {
            content: '';
            position: absolute;
            top: -50%;
            right: -50%;
            width: 100%;
            height: 100%;
            background: radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, transparent 70%);
            opacity: 0;
            transition: opacity 0.5s;
        }
        
        .security-card:hover::before {
            opacity: 1;
        }
        
        .security-card:hover {
            transform: translateY(-5px);
            border-color: rgba(102, 126, 234, 0.3);
        }
        
        .card-header {
            display: flex;
            align-items: center;
            gap: 1rem;
            margin-bottom: 1.5rem;
            position: relative;
            z-index: 1;
        }
        
        .card-icon {
            width: 50px;
            height: 50px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.5rem;
        }
        
        .card-title {
            font-size: 1.3rem;
            font-weight: bold;
        }
        
        .card-status {
            margin-left: auto;
            padding: 0.3rem 0.8rem;
            border-radius: 6px;
            font-size: 0.8rem;
            font-weight: bold;
        }
        
        .status-active {
            background: rgba(67, 232, 169, 0.2);
            color: #67e8a9;
        }
        
        .status-warning {
            background: rgba(237, 137, 54, 0.2);
            color: #ed8936;
        }
        
        .card-description {
            color: #bbb;
            margin-bottom: 1.5rem;
            position: relative;
            z-index: 1;
        }
        
        .card-features {
            list-style: none;
            position: relative;
            z-index: 1;
        }
        
        .card-features li {
            padding: 0.5rem 0;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            color: #ccc;
            font-size: 0.9rem;
        }
        
        .card-features li::before {
            content: '✓';
            color: #67e8a9;
            font-weight: bold;
        }
        
        /* Compliance Table */
        .compliance-table {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            overflow: hidden;
            margin: 3rem 0;
        }
        
        .table-header {
            background: rgba(102, 126, 234, 0.1);
            padding: 1.5rem;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .table-title {
            font-size: 1.5rem;
            font-weight: bold;
            margin-bottom: 0.5rem;
        }
        
        .table-subtitle {
            color: #888;
        }
        
        .compliance-grid {
            display: grid;
            grid-template-columns: 1fr 120px 120px 150px;
            gap: 1rem;
            padding: 1rem;
            border-bottom: 1px solid rgba(255, 255, 255, 0.05);
            align-items: center;
        }
        
        .compliance-grid:last-child {
            border-bottom: none;
        }
        
        .compliance-grid:hover {
            background: rgba(255, 255, 255, 0.05);
        }
        
        .compliance-name {
            font-weight: 600;
        }
        
        .compliance-requirement {
            color: #888;
            font-size: 0.9rem;
        }
        
        .compliance-status {
            padding: 0.4rem 0.8rem;
            border-radius: 6px;
            font-size: 0.8rem;
            font-weight: bold;
            text-align: center;
        }
        
        .status-compliant {
            background: rgba(67, 232, 169, 0.2);
            color: #67e8a9;
        }
        
        .status-partial {
            background: rgba(237, 137, 54, 0.2);
            color: #ed8936;
        }
        
        .status-pending {
            background: rgba(255, 255, 255, 0.1);
            color: #888;
        }
        
        .compliance-score {
            text-align: center;
            font-weight: bold;
            color: #667eea;
        }
        
        /* Audit Trail */
        .audit-section {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            margin: 3rem 0;
        }
        
        .audit-controls {
            display: flex;
            justify-content: between;
            align-items: center;
            margin-bottom: 2rem;
            flex-wrap: wrap;
            gap: 1rem;
        }
        
        .audit-filters {
            display: flex;
            gap: 1rem;
            flex-wrap: wrap;
        }
        
        .filter-select {
            padding: 0.5rem 1rem;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 6px;
            color: white;
            font-size: 0.9rem;
        }
        
        .audit-export {
            padding: 0.5rem 1.5rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-weight: 600;
            transition: all 0.3s;
        }
        
        .audit-export:hover {
            transform: translateY(-2px);
            box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3);
        }
        
        .audit-log {
            background: #1a1a1a;
            border: 1px solid #333;
            border-radius: 8px;
            max-height: 400px;
            overflow-y: auto;
            font-family: monospace;
            font-size: 0.9rem;
        }
        
        .log-entry {
            display: grid;
            grid-template-columns: 150px 100px 120px 1fr 100px;
            gap: 1rem;
            padding: 1rem;
            border-bottom: 1px solid #333;
            align-items: center;
        }
        
        .log-entry:hover {
            background: rgba(255, 255, 255, 0.05);
        }
        
        .log-entry:last-child {
            border-bottom: none;
        }
        
        .log-timestamp {
            color: #667eea;
        }
        
        .log-user {
            color: #82aaff;
        }
        
        .log-action {
            color: #c3e88d;
        }
        
        .log-details {
            color: #e0e0e0;
        }
        
        .log-ip {
            color: #888;
            font-size: 0.8rem;
        }
        
        /* Risk Assessment */
        .risk-matrix {
            display: grid;
            grid-template-columns: repeat(5, 1fr);
            gap: 1px;
            background: #333;
            border-radius: 8px;
            overflow: hidden;
            margin: 2rem 0;
        }
        
        .risk-cell {
            aspect-ratio: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 0.8rem;
            cursor: pointer;
            transition: all 0.3s;
        }
        
        .risk-cell:hover {
            transform: scale(1.1);
            z-index: 1;
        }
        
        .risk-low { background: #67e8a9; color: #0a0a0a; }
        .risk-medium { background: #f6e05e; color: #0a0a0a; }
        .risk-high { background: #ed8936; color: white; }
        .risk-critical { background: #f56565; color: white; }
        
        .risk-legend {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 1rem;
            margin-top: 1rem;
        }
        
        .legend-item {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-size: 0.9rem;
        }
        
        .legend-color {
            width: 16px;
            height: 16px;
            border-radius: 2px;
        }
        
        /* Encryption Details */
        .encryption-details {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            margin: 2rem 0;
        }
        
        .encryption-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 2rem;
            margin-top: 2rem;
        }
        
        .encryption-item {
            background: rgba(255, 255, 255, 0.05);
            padding: 1.5rem;
            border-radius: 12px;
            border: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .encryption-title {
            font-weight: bold;
            margin-bottom: 1rem;
            color: #667eea;
        }
        
        .encryption-spec {
            display: flex;
            justify-content: between;
            align-items: center;
            padding: 0.5rem 0;
            border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }
        
        .encryption-spec:last-child {
            border-bottom: none;
        }
        
        .spec-label {
            color: #bbb;
        }
        
        .spec-value {
            color: white;
            font-weight: 600;
        }
        
        /* Alert Boxes */
        .alert-box {
            padding: 1.5rem;
            border-radius: 12px;
            margin: 2rem 0;
            display: flex;
            align-items: start;
            gap: 1rem;
        }
        
        .alert-critical {
            background: rgba(245, 101, 101, 0.1);
            border: 1px solid rgba(245, 101, 101, 0.3);
        }
        
        .alert-warning {
            background: rgba(237, 137, 54, 0.1);
            border: 1px solid rgba(237, 137, 54, 0.3);
        }
        
        .alert-info {
            background: rgba(102, 126, 234, 0.1);
            border: 1px solid rgba(102, 126, 234, 0.3);
        }
        
        .alert-success {
            background: rgba(67, 232, 169, 0.1);
            border: 1px solid rgba(67, 232, 169, 0.3);
        }
        
        .alert-icon {
            font-size: 1.5rem;
            flex-shrink: 0;
            margin-top: 0.2rem;
        }
        
        .alert-content h4 {
            margin-bottom: 0.5rem;
        }
        
        /* Responsive */
        @media (max-width: 768px) {
            .security-score {
                flex-direction: column;
                gap: 1rem;
            }
            
            .compliance-grid {
                grid-template-columns: 1fr;
                gap: 0.5rem;
            }
            
            .log-entry {
                grid-template-columns: 1fr;
                gap: 0.5rem;
            }
            
            .risk-matrix {
                grid-template-columns: repeat(3, 1fr);
            }
        }
      `}</style>

      {/* Navigation */}
      <nav>
        <div className="nav-container">
          <a href="index.html" className="back-link">
            <ChevronLeft size={16} /> Retour à l'accueil
          </a>
          <div>Sécurité et Conformité</div>
        </div>
      </nav>
      
      {/* Main Content */}
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <h1 className="page-title">Sécurité et Conformité</h1>
          <p className="page-subtitle">Protection des données et respect des réglementations bancaires</p>
        </div>
        
        {/* Security Status */}
        <div className="security-status">
          <div className="status-icon">🛡️</div>
          <h2 className="status-title">Système Sécurisé et Conforme</h2>
          <p className="status-description">
            Toutes les mesures de sécurité sont actives et conformes aux standards bancaires BEAC/BCEAO
          </p>
          <div className="security-score">
            <div className="score-item">
              <div className="score-value">A+</div>
              <div className="score-label">Security Score</div>
            </div>
            <div className="score-item">
              <div className="score-value">100%</div>
              <div className="score-label">Conformité BEAC</div>
            </div>
            <div className="score-item">
              <div className="score-value">0</div>
              <div className="score-label">Vulnérabilités</div>
            </div>
            <div className="score-item">
              <div className="score-value">24/7</div>
              <div className="score-label">Monitoring</div>
            </div>
          </div>
        </div>
        
        {/* Section Tabs */}
        <div className="section-tabs">
          <button 
            className={`tab-button ${activeSection === 'authentication' ? 'active' : ''}`} 
            onClick={() => showSection('authentication')}
          >
            Authentification
          </button>
          <button 
            className={`tab-button ${activeSection === 'encryption' ? 'active' : ''}`} 
            onClick={() => showSection('encryption')}
          >
            Chiffrement
          </button>
          <button 
            className={`tab-button ${activeSection === 'compliance' ? 'active' : ''}`} 
            onClick={() => showSection('compliance')}
          >
            Conformité
          </button>
          <button 
            className={`tab-button ${activeSection === 'audit' ? 'active' : ''}`} 
            onClick={() => showSection('audit')}
          >
            Audit Trail
          </button>
          <button 
            className={`tab-button ${activeSection === 'risks' ? 'active' : ''}`} 
            onClick={() => showSection('risks')}
          >
            Gestion des Risques
          </button>
        </div>
        
        {/* Authentication Section */}
        <div className={`content-section ${activeSection === 'authentication' ? 'active' : ''}`}>
          <h2 style={{ marginBottom: '2rem' }}>Authentification et Contrôle d'Accès</h2>
          
          <div className="security-grid">
            <div className="security-card">
              <div className="card-header">
                <div className="card-icon">🔐</div>
                <div className="card-title">JWT Authentication</div>
                <div className="card-status status-active">ACTIF</div>
              </div>
              <div className="card-description">
                Authentification basée sur des tokens JWT sécurisés avec rotation automatique et expiration configurable.
              </div>
              <ul className="card-features">
                <li>Tokens signés RS256</li>
                <li>Expiration automatique (24h)</li>
                <li>Refresh token sécurisé</li>
                <li>Révocation immédiate</li>
                <li>Claims personnalisés</li>
              </ul>
            </div>
            
            <div className="security-card">
              <div className="card-header">
                <div className="card-icon">👥</div>
                <div className="card-title">RBAC (Role-Based Access)</div>
                <div className="card-status status-active">ACTIF</div>
              </div>
              <div className="card-description">
                Contrôle d'accès granulaire basé sur les rôles utilisateurs avec permissions fine-grained.
              </div>
              <ul className="card-features">
                <li>4 profils principaux (IT, BackOffice, AMOA, MOE)</li>
                <li>Permissions par endpoint</li>
                <li>Hiérarchie des rôles</li>
                <li>Délégation temporaire</li>
                <li>Audit des accès</li>
              </ul>
            </div>
            
            <div className="security-card">
              <div className="card-header">
                <div className="card-icon">🔒</div>
                <div className="card-title">Multi-Factor Authentication</div>
                <div className="card-status status-warning">OPTIONNEL</div>
              </div>
              <div className="card-description">
                Authentification à deux facteurs pour renforcer la sécurité des comptes sensibles.
              </div>
              <ul className="card-features">
                <li>TOTP (Google Authenticator)</li>
                <li>SMS fallback</li>
                <li>Code de récupération</li>
                <li>Obligatoire pour admins</li>
                <li>Session mémorisée (30j)</li>
              </ul>
            </div>
            
            <div className="security-card">
              <div className="card-header">
                <div className="card-icon">🌐</div>
                <div className="card-title">SSO Integration</div>
                <div className="card-status status-active">ACTIF</div>
              </div>
              <div className="card-description">
                Intégration avec les systèmes d'authentification existants de l'entreprise (LDAP/Active Directory).
              </div>
              <ul className="card-features">
                <li>Active Directory sync</li>
                <li>LDAP authentication</li>
                <li>SAML 2.0 support</li>
                <li>Groupes automatiques</li>
                <li>Provisioning utilisateurs</li>
              </ul>
            </div>
          </div>
          
          <div className="alert-box alert-info">
            <div className="alert-icon">ℹ️</div>
            <div>
              <h4>Configuration Recommandée</h4>
              <p>Pour un environnement de production, nous recommandons d'activer le MFA pour tous les utilisateurs ayant accès aux données sensibles et de configurer l'intégration SSO avec votre Active Directory existant.</p>
            </div>
          </div>
        </div>
        
        {/* Encryption Section */}
        <div className={`content-section ${activeSection === 'encryption' ? 'active' : ''}`}>
          <h2 style={{ marginBottom: '2rem' }}>Chiffrement des Données</h2>
          
          <div className="encryption-details">
            <h3>Standards de Chiffrement Appliqués</h3>
            <div className="encryption-grid">
              <div className="encryption-item">
                <h4 className="encryption-title">🔐 Données au Repos</h4>
                <div className="encryption-spec">
                  <span className="spec-label">Algorithme</span>
                  <span className="spec-value">AES-256-GCM</span>
                </div>
                <div className="encryption-spec">
                  <span className="spec-label">Gestion des clés</span>
                  <span className="spec-value">HSM / Key Vault</span>
                </div>
                <div className="encryption-spec">
                  <span className="spec-label">Rotation</span>
                  <span className="spec-value">Automatique (90j)</span>
                </div>
                <div className="encryption-spec">
                  <span className="spec-label">Backup clés</span>
                  <span className="spec-value">Escrow sécurisé</span>
                </div>
              </div>
              
              <div className="encryption-item">
                <h4 className="encryption-title">🌐 Données en Transit</h4>
                <div className="encryption-spec">
                  <span className="spec-label">Protocole</span>
                  <span className="spec-value">TLS 1.3</span>
                </div>
                <div className="encryption-spec">
                  <span className="spec-label">Certificats</span>
                  <span className="spec-value">ECC P-256</span>
                </div>
                <div className="encryption-spec">
                  <span className="spec-label">Perfect Forward Secrecy</span>
                  <span className="spec-value">Activé</span>
                </div>
                <div className="encryption-spec">
                  <span className="spec-label">HSTS</span>
                  <span className="spec-value">Strict</span>
                </div>
              </div>
              
              <div className="encryption-item">
                <h4 className="encryption-title">🔍 Données en Traitement</h4>
                <div className="encryption-spec">
                  <span className="spec-label">Mémoire</span>
                  <span className="spec-value">Chiffrement RAM</span>
                </div>
                <div className="encryption-spec">
                  <span className="spec-label">Processus</span>
                  <span className="spec-value">Isolation containers</span>
                </div>
                <div className="encryption-spec">
                  <span className="spec-label">Logs</span>
                  <span className="spec-value">Masquage auto</span>
                </div>
                <div className="encryption-spec">
                  <span className="spec-label">Temporary files</span>
                  <span className="spec-value">Suppression sécurisée</span>
                </div>
              </div>
              
              <div className="encryption-item">
                <h4 className="encryption-title">🔑 Gestion des Clés</h4>
                <div className="encryption-spec">
                  <span className="spec-label">HSM</span>
                  <span className="spec-value">FIPS 140-2 Level 3</span>
                </div>
                <div className="encryption-spec">
                  <span className="spec-label">Longueur clés</span>
                  <span className="spec-value">256 bits minimum</span>
                </div>
                <div className="encryption-spec">
                  <span className="spec-label">Dérivation</span>
                  <span className="spec-value">PBKDF2 / Argon2</span>
                </div>
                <div className="encryption-spec">
                  <span className="spec-label">Audit clés</span>
                  <span className="spec-value">Traçabilité complète</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="alert-box alert-success">
            <div className="alert-icon">✅</div>
            <div>
              <h4>Certification de Sécurité</h4>
              <p>Notre implémentation de chiffrement respecte les standards NIST, ISO 27001 et les exigences BEAC/BCEAO. Audit de sécurité externe réalisé par [Nom du cabinet] en décembre 2024.</p>
            </div>
          </div>
        </div>
        
        {/* Compliance Section */}
        <div className={`content-section ${activeSection === 'compliance' ? 'active' : ''}`}>
          <h2 style={{ marginBottom: '2rem' }}>Conformité Réglementaire</h2>
          
          <div className="compliance-table">
            <div className="table-header">
              <h3 className="table-title">État de Conformité par Réglementation</h3>
              <p className="table-subtitle">Dernière évaluation: 15 décembre 2024</p>
            </div>
            
            <div className="compliance-grid" style={{ fontWeight: 'bold', background: 'rgba(255,255,255,0.05)' }}>
              <div>Réglementation</div>
              <div style={{ textAlign: 'center' }}>Statut</div>
              <div style={{ textAlign: 'center' }}>Score</div>
              <div style={{ textAlign: 'center' }}>Prochaine Révision</div>
            </div>
            
            <div className="compliance-grid">
              <div>
                <div className="compliance-name">BEAC - Réglementation Bancaire</div>
                <div className="compliance-requirement">Circulaires n°2023-15, 2023-22, 2024-03</div>
              </div>
              <div className="compliance-status status-compliant">CONFORME</div>
              <div className="compliance-score">100%</div>
              <div style={{ color: '#888' }}>Mars 2025</div>
            </div>
            
            <div className="compliance-grid">
              <div>
                <div className="compliance-name">BCEAO - Système de Paiement</div>
                <div className="compliance-requirement">Instructions n°2024-01 à 2024-08</div>
              </div>
              <div className="compliance-status status-compliant">CONFORME</div>
              <div className="compliance-score">98%</div>
              <div style={{ color: '#888' }}>Avril 2025</div>
            </div>
            
            <div className="compliance-grid">
              <div>
                <div className="compliance-name">COBAC - Supervision Bancaire</div>
                <div className="compliance-requirement">Règlements prudentiels</div>
              </div>
              <div className="compliance-status status-compliant">CONFORME</div>
              <div className="compliance-score">96%</div>
              <div style={{ color: '#888' }}>Juin 2025</div>
            </div>
            
            <div className="compliance-grid">
              <div>
                <div className="compliance-name">OHADA - Droit des Affaires</div>
                <div className="compliance-requirement">Acte uniforme, Traité révisé</div>
              </div>
              <div className="compliance-status status-partial">PARTIEL</div>
              <div className="compliance-score">87%</div>
              <div style={{ color: '#888' }}>Février 2025</div>
            </div>
            
            <div className="compliance-grid">
              <div>
                <div className="compliance-name">ISO 27001 - Sécurité Information</div>
                <div className="compliance-requirement">Management sécurité SI</div>
              </div>
              <div className="compliance-status status-compliant">CONFORME</div>
              <div className="compliance-score">94%</div>
              <div style={{ color: '#888' }}>Août 2025</div>
            </div>
            
            <div className="compliance-grid">
              <div>
                <div className="compliance-name">RGPD - Protection Données</div>
                <div className="compliance-requirement">Règlement UE 2016/679</div>
              </div>
              <div className="compliance-status status-pending">EN COURS</div>
              <div className="compliance-score">78%</div>
              <div style={{ color: '#888' }}>Janvier 2025</div>
            </div>
          </div>
          
          <div className="alert-box alert-warning">
            <div className="alert-icon">⚠️</div>
            <div>
              <h4>Actions Requises</h4>
              <p><strong>OHADA:</strong> Mise à jour des contrats fournisseurs selon le nouveau cadre légal.<br />
              <strong>RGPD:</strong> Finalisation des procédures de gestion des droits des personnes concernées.</p>
            </div>
          </div>
        </div>
        
        {/* Audit Trail Section */}
        <div className={`content-section ${activeSection === 'audit' ? 'active' : ''}`}>
          <h2 style={{ marginBottom: '2rem' }}>Piste d'Audit (Audit Trail)</h2>
          
          <div className="audit-section">
            <div className="audit-controls">
              <div className="audit-filters">
                <select className="filter-select">
                  <option>Toutes les actions</option>
                  <option>Authentification</option>
                  <option>Accès données</option>
                  <option>Modifications</option>
                  <option>Erreurs</option>
                </select>
                <select className="filter-select">
                  <option>Dernières 24h</option>
                  <option>7 derniers jours</option>
                  <option>30 derniers jours</option>
                  <option>Période personnalisée</option>
                </select>
                <select className="filter-select">
                  <option>Tous les utilisateurs</option>
                  <option>Administrateurs</option>
                  <option>Utilisateurs IT</option>
                  <option>BackOffice</option>
                </select>
              </div>
              <button className="audit-export" onClick={exportAudit}>📥 Exporter</button>
            </div>
            
            <div className="audit-log">
              <div className="log-entry" style={{ fontWeight: 'bold', background: 'rgba(255,255,255,0.05)' }}>
                <div>Timestamp</div>
                <div>Utilisateur</div>
                <div>Action</div>
                <div>Détails</div>
                <div>IP Source</div>
              </div>
              
              <div className="log-entry">
                <div className="log-timestamp">2024-12-15 14:32:18</div>
                <div className="log-user">alice.martin</div>
                <div className="log-action">QUERY</div>
                <div className="log-details">Recherche: "chèque impayé BEAC" - Réponse générée (2.1s)</div>
                <div className="log-ip">192.168.1.45</div>
              </div>
              
              <div className="log-entry">
                <div className="log-timestamp">2024-12-15 14:31:47</div>
                <div className="log-user">bob.dupont</div>
                <div className="log-action">LOGIN</div>
                <div className="log-details">Authentification réussie - Profil IT</div>
                <div className="log-ip">10.0.2.134</div>
              </div>
              
              <div className="log-entry">
                <div className="log-timestamp">2024-12-15 14:30:22</div>
                <div className="log-user">claire.bernard</div>
                <div className="log-action">FEEDBACK</div>
                <div className="log-details">Note: 5/5 - Query ID: q_1734268822</div>
                <div className="log-ip">172.16.0.89</div>
              </div>
              
              <div className="log-entry">
                <div className="log-timestamp">2024-12-15 14:29:15</div>
                <div className="log-user">system</div>
                <div className="log-action">INDEX_UPDATE</div>
                <div className="log-details">Synchronisation incrémentale - 147 documents mis à jour</div>
                <div className="log-ip">127.0.0.1</div>
              </div>
              
              <div className="log-entry">
                <div className="log-timestamp">2024-12-15 14:28:03</div>
                <div className="log-user">admin</div>
                <div className="log-action">CONFIG_CHANGE</div>
                <div className="log-details">Modification cache TTL: 3600s -&gt; 7200s</div>
                <div className="log-ip">10.0.1.100</div>
              </div>
              
              <div className="log-entry">
                <div className="log-timestamp">2024-12-15 14:27:41</div>
                <div className="log-user">david.moreau</div>
                <div className="log-action">ERROR</div>
                <div className="log-details">Tentative accès non autorisé - Endpoint /admin/stats</div>
                <div className="log-ip">192.168.1.78</div>
              </div>
            </div>
          </div>
          
          <div className="alert-box alert-info">
            <div className="alert-icon">ℹ️</div>
            <div>
              <h4>Conservation des Logs</h4>
              <p>Les logs d'audit sont conservés pendant 7 ans conformément aux exigences réglementaires BEAC. Sauvegarde automatique quotidienne avec chiffrement AES-256. Accès en lecture seule pour les auditeurs externes.</p>
            </div>
          </div>
        </div>
        
        {/* Risk Management Section */}
        <div className={`content-section ${activeSection === 'risks' ? 'active' : ''}`}>
          <h2 style={{ marginBottom: '2rem' }}>Gestion des Risques</h2>
          
          <div style={{ marginBottom: '3rem' }}>
            <h3>Matrice des Risques</h3>
            <p style={{ color: '#888', marginBottom: '2rem' }}>
              Évaluation des risques selon la probabilité d'occurrence et l'impact potentiel
            </p>
            
            <div className="risk-matrix">
              {['L', 'L', 'M', 'H', 'C',
                'L', 'M', 'M', 'H', 'C',
                'M', 'M', 'H', 'H', 'C',
                'H', 'H', 'H', 'C', 'C',
                'C', 'C', 'C', 'C', 'C'].map((level, index) => (
                <div 
                  key={index}
                  className={`risk-cell risk-${level === 'L' ? 'low' : level === 'M' ? 'medium' : level === 'H' ? 'high' : 'critical'}`}
                  onClick={() => handleRiskCellClick(level)}
                >
                  {level}
                </div>
              ))}
            </div>
            
            <div className="risk-legend">
              <div className="legend-item">
                <div className="legend-color risk-low"></div>
                <span>Faible (L)</span>
              </div>
              <div className="legend-item">
                <div className="legend-color risk-medium"></div>
                <span>Moyen (M)</span>
              </div>
              <div className="legend-item">
                <div className="legend-color risk-high"></div>
                <span>Élevé (H)</span>
              </div>
              <div className="legend-item">
                <div className="legend-color risk-critical"></div>
                <span>Critique (C)</span>
              </div>
            </div>
          </div>
          
          <div className="security-grid">
            <div className="security-card">
              <div className="card-header">
                <div className="card-icon">🔒</div>
                <div className="card-title">Risques de Sécurité</div>
                <div className="card-status status-active">SURVEILLÉ</div>
              </div>
              <div className="card-description">
                Menaces identifiées et mesures de mitigation en place pour protéger l'intégrité du système.
              </div>
              <ul className="card-features">
                <li>Intrusion / Tentatives d'accès non autorisé</li>
                <li>Fuite de données sensibles</li>
                <li>Attaques par déni de service (DDoS)</li>
                <li>Compromission des clés de chiffrement</li>
                <li>Injection de code malveillant</li>
              </ul>
            </div>
            
            <div className="security-card">
              <div className="card-header">
                <div className="card-icon">⚖️</div>
                <div className="card-title">Risques de Conformité</div>
                <div className="card-status status-warning">ATTENTION</div>
              </div>
              <div className="card-description">
                Risques liés au non-respect des réglementations bancaires et leurs conséquences potentielles.
              </div>
              <ul className="card-features">
                <li>Non-conformité BEAC/BCEAO</li>
                <li>Violations RGPD</li>
                <li>Audit réglementaire défavorable</li>
                <li>Sanctions financières</li>
                <li>Perte de licence bancaire</li>
              </ul>
            </div>
            
            <div className="security-card">
              <div className="card-header">
                <div className="card-icon">🏗️</div>
                <div className="card-title">Risques Opérationnels</div>
                <div className="card-status status-active">MAÎTRISÉ</div>
              </div>
              <div className="card-description">
                Risques liés au fonctionnement quotidien du système et à la continuité de service.
              </div>
              <ul className="card-features">
                <li>Panne système / Indisponibilité</li>
                <li>Corruption de données</li>
                <li>Performance dégradée</li>
                <li>Erreurs de configuration</li>
                <li>Perte de compétences clés</li>
              </ul>
            </div>
            
            <div className="security-card">
              <div className="card-header">
                <div className="card-icon">🔄</div>
                <div className="card-title">Plan de Continuité</div>
                <div className="card-status status-active">TESTÉ</div>
              </div>
              <div className="card-description">
                Mesures de continuité d'activité et de reprise après incident pour assurer la résilience du système.
              </div>
              <ul className="card-features">
                <li>Sauvegarde automatique (4x/jour)</li>
                <li>Site de secours (RTO: 4h, RPO: 1h)</li>
                <li>Plan de reprise documenté</li>
                <li>Tests trimestriels</li>
                <li>Équipe d'astreinte 24/7</li>
              </ul>
            </div>
          </div>
          
          <div className="alert-box alert-critical">
            <div className="alert-icon">🚨</div>
            <div>
              <h4>Plan d'Action Prioritaire</h4>
              <p><strong>Échéance: 31 janvier 2025</strong><br />
              Finalisation de la mise en conformité RGPD et formation des équipes aux nouvelles procédures OHADA. Test du plan de continuité prévu le 20 janvier 2025.</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SecurityCompliance;